import type {
  LearningAnswerOption,
  LearningContentItem,
  LearningLessonDetail,
} from '@services/firebase/learning'
import {
  normalizeLearningLocale,
  resolveLocalizedDisplayText,
  resolveLocalizedMapText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import {
  getVisibleAnswers,
  getVisibleContent,
  getVisibleQuestions,
  resolveContentTitle,
  resolveContentTranslation,
} from '@features/learning/shared/online-book-content/primitives'
import { resolveOnlineBookTemplate } from '@features/learning/shared/online-book-template'
import {
  resolveTeachingPresentationSlideTemplate,
  type TeachingPresentationSlideTemplate,
} from './presentation-template-registry'

const MAX_ITEMS_PER_SLIDE = 4
const MAX_TEXT_CHARS_PER_SLIDE = 900

export type TeachingPresentationSlideKind =
  | 'cards'
  | 'image'
  | 'multiple_choice'
  | 'text'
  | 'title'

export interface TeachingPresentationCard {
  readonly audio?: string
  readonly image?: string
  readonly isRight?: boolean
  readonly primary: string
  readonly secondary?: string
  readonly speaker?: string
  readonly type?: string
}

export interface TeachingPresentationChoice {
  readonly isCorrect: boolean
  readonly label: string
}

interface TeachingPresentationSlideBase {
  readonly id: string
  readonly sectionId: string
  readonly slideTemplate: TeachingPresentationSlideTemplate
  readonly sourceBlockId: string
  readonly sourceTemplate: string
  readonly title?: string
  readonly kind: TeachingPresentationSlideKind
}

export interface TeachingPresentationTitleSlide extends TeachingPresentationSlideBase {
  readonly kind: 'title'
}

export interface TeachingPresentationTextSlide extends TeachingPresentationSlideBase {
  readonly html: string
  readonly image?: string
  readonly kind: 'text'
}

export interface TeachingPresentationCardsSlide extends TeachingPresentationSlideBase {
  readonly cards: readonly TeachingPresentationCard[]
  readonly image?: string
  readonly kind: 'cards'
}

export interface TeachingPresentationImageSlide extends TeachingPresentationSlideBase {
  readonly caption?: string
  readonly image: string
  readonly kind: 'image'
}

export interface TeachingPresentationMultipleChoiceSlide
  extends TeachingPresentationSlideBase {
  readonly choices: readonly TeachingPresentationChoice[]
  readonly kind: 'multiple_choice'
  readonly prompt: string
}

export type TeachingPresentationSlide =
  | TeachingPresentationCardsSlide
  | TeachingPresentationImageSlide
  | TeachingPresentationMultipleChoiceSlide
  | TeachingPresentationTextSlide
  | TeachingPresentationTitleSlide

export interface TeachingPresentationSection {
  readonly id: string
  readonly slideIds: readonly string[]
  readonly sourceBlockIds: readonly string[]
  readonly title: string
}

export interface SkippedTeachingPresentationBlock {
  readonly blockId: string
  readonly reason: 'empty' | 'horizontal_rule' | 'unsupported_template'
  readonly template: string
}

export interface TeachingPresentationDeck {
  readonly lessonId: string
  readonly sections: readonly TeachingPresentationSection[]
  readonly slides: readonly TeachingPresentationSlide[]
  readonly skippedBlocks: readonly SkippedTeachingPresentationBlock[]
  readonly title: string
}

interface MutableSection {
  readonly id: string
  readonly slideIds: string[]
  readonly sourceBlockIds: string[]
  readonly title: string
}

function hasText(value: string | undefined): value is string {
  return Boolean(value?.trim())
}

function resolveBlockTitle(block: LearningLessonDetail, locale: string) {
  return resolveLocalizedDisplayText(block.title, locale, block.text)
}

function resolveItemText(item: LearningContentItem, locale: string) {
  return resolveContentTitle(item, locale) ?? item.text
}

function resolveItemHtml(item: LearningContentItem, locale: string) {
  return item.data?.text ?? resolveItemText(item, locale)
}

function resolveBlockImage(block: LearningLessonDetail) {
  return block.featuredImage ?? block.image ?? block.avatar ?? block.thumbnail
}

function splitHtmlIntoChunks(html: string) {
  const paragraphs = html.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi)

  if (!paragraphs || paragraphs.length <= 1) {
    return [html]
  }

  const chunks: string[] = []
  let current: string[] = []
  let currentLength = 0

  for (const paragraph of paragraphs) {
    if (
      current.length > 0 &&
      currentLength + paragraph.length > MAX_TEXT_CHARS_PER_SLIDE
    ) {
      chunks.push(current.join(''))
      current = []
      currentLength = 0
    }

    current.push(paragraph)
    currentLength += paragraph.length
  }

  if (current.length > 0) {
    chunks.push(current.join(''))
  }

  return chunks.length > 0 ? chunks : [html]
}

function chunkItems<T>(items: readonly T[]) {
  const chunks: T[][] = []

  for (let index = 0; index < items.length; index += MAX_ITEMS_PER_SLIDE) {
    chunks.push(items.slice(index, index + MAX_ITEMS_PER_SLIDE))
  }

  return chunks
}

function resolveAnswerLabel(option: LearningAnswerOption | string, locale: string) {
  if (typeof option === 'string') {
    return option
  }

  if (normalizeLearningLocale(locale) === 'es') {
    const sourceAnswer = [option.answer, option.text].find(hasText)
    if (sourceAnswer) {
      return sourceAnswer
    }
  }

  return (
    resolveLocalizedDisplayText(option.label, locale) ??
    resolveLocalizedDisplayText(option.content, locale) ??
    resolveLocalizedMapText(option.translate, locale) ??
    option.text ??
    option.answer ??
    option.value ??
    'Option'
  )
}

function resolveAnswerCorrectness(option: LearningAnswerOption | string) {
  if (typeof option === 'string') {
    return false
  }

  return option.correct === true || option.isRight === true || option.result === true
}

function resolveQuestionText(block: LearningLessonDetail, locale: string) {
  const question = getVisibleQuestions(block.questions)[0]

  return (
    resolveLocalizedDisplayText(question?.text, locale) ??
    resolveLocalizedMapText(question?.translate, locale) ??
    resolveBlockTitle(block, locale) ??
    'Question'
  )
}

function resolveCard(item: LearningContentItem, locale: string): TeachingPresentationCard | null {
  const primary = resolveItemText(item, locale)
  const secondary = resolveContentTranslation(item, locale)

  if (!hasText(primary)) {
    return null
  }

  return {
    ...(item.audio ? { audio: item.audio } : {}),
    ...(item.image || item.featuredImage || item.avatar
      ? { image: item.image ?? item.featuredImage ?? item.avatar }
      : {}),
    ...(item.isRight !== undefined ? { isRight: item.isRight } : {}),
    primary,
    ...(secondary ? { secondary } : {}),
    ...(item.type ? { type: item.type } : {}),
  }
}

function resolveConversationCard(item: LearningContentItem, locale: string): TeachingPresentationCard | readonly TeachingPresentationCard[] | null {
  const conversation = item.conversation ?? []

  if (conversation.length === 0) {
    return resolveCard(item, locale)
  }

  const cards: Array<TeachingPresentationCard | null> = conversation.map((line): TeachingPresentationCard | null => {
    const primary = line.text ?? line.answer ?? line.name
    const secondary =
      resolveTranslatedText(line.translates, locale) ??
      resolveLocalizedMapText(line.translate, locale)
    const speaker = line.name ?? line.speaker ?? (line.isRight ? 'B' : 'A')

    if (!hasText(primary)) {
      return null
    }

    return {
      ...(line.audio ? { audio: line.audio } : {}),
      ...(line.image || line.avatar ? { image: line.image ?? line.avatar } : {}),
      ...(line.isRight !== undefined ? { isRight: line.isRight } : {}),
      primary,
      ...(secondary ? { secondary } : {}),
      speaker,
    }
  })

  return cards.filter((card): card is TeachingPresentationCard => card !== null)
}

function resolveConversationCards(item: LearningContentItem, locale: string): readonly TeachingPresentationCard[] {
  const resolved = resolveConversationCard(item, locale)

  if (!resolved) {
    return []
  }

  return 'primary' in resolved ? [resolved] : resolved
}

function createSection(
  sections: MutableSection[],
  title: string,
  blockId: string,
): MutableSection {
  const section: MutableSection = {
    id: `section-${sections.length + 1}`,
    slideIds: [],
    sourceBlockIds: [blockId],
    title,
  }
  sections.push(section)
  return section
}

export function buildTeachingPresentationDeck({
  blocks,
  lessonId,
  title,
  locale,
}: {
  readonly blocks: readonly LearningLessonDetail[]
  readonly lessonId: string
  readonly locale: string
  readonly title: string
}): TeachingPresentationDeck {
  const slides: TeachingPresentationSlide[] = []
  const sections: MutableSection[] = []
  const skippedBlocks: SkippedTeachingPresentationBlock[] = []
  let currentSection = createSection(sections, title, 'lesson')

  const addSlide = (slide: TeachingPresentationSlide) => {
    slides.push(slide)
    currentSection.slideIds.push(slide.id)

    if (!currentSection.sourceBlockIds.includes(slide.sourceBlockId)) {
      currentSection.sourceBlockIds.push(slide.sourceBlockId)
    }
  }

  for (const block of blocks) {
    const template = resolveOnlineBookTemplate(block)
    const blockTitle = resolveBlockTitle(block, locale)

    if (template === 'heading' || template === 'subtitle') {
      currentSection = createSection(
        sections,
        blockTitle ?? `Section ${sections.length + 1}`,
        block.id,
      )
      if (blockTitle) {
        addSlide({
          id: `${block.id}-title`,
          kind: 'title',
          sectionId: currentSection.id,
          slideTemplate: 'title',
          sourceBlockId: block.id,
          sourceTemplate: template,
          title: blockTitle,
        })
      }
      continue
    }

    if (template === 'horizontal_rule') {
      currentSection.sourceBlockIds.push(block.id)
      skippedBlocks.push({ blockId: block.id, reason: 'horizontal_rule', template })
      continue
    }

    if (template === 'image') {
      const image = resolveBlockImage(block)

      if (image) {
        addSlide({
          caption: blockTitle,
          id: `${block.id}-image`,
          image,
          kind: 'image',
          sectionId: currentSection.id,
          slideTemplate: 'image',
          sourceBlockId: block.id,
          sourceTemplate: template,
          title: blockTitle,
        })
      } else {
        skippedBlocks.push({ blockId: block.id, reason: 'empty', template })
      }
      continue
    }

    if (template === 'multiple_choice') {
      const choices = getVisibleAnswers(block.answers).map((option) => ({
        isCorrect: resolveAnswerCorrectness(option),
        label: resolveAnswerLabel(option, locale),
      }))

      if (choices.length > 0) {
        addSlide({
          choices,
          id: `${block.id}-multiple-choice`,
          kind: 'multiple_choice',
          prompt: resolveQuestionText(block, locale),
          sectionId: currentSection.id,
          slideTemplate: 'multiple-choice',
          sourceBlockId: block.id,
          sourceTemplate: template,
          title: blockTitle,
        })
      } else {
        skippedBlocks.push({ blockId: block.id, reason: 'empty', template })
      }
      continue
    }

    if (template === 'blog') {
      const content = getVisibleContent(block.content)
      const textEntries = content.flatMap((item) => {
        const html = resolveItemHtml(item, locale)
        return hasText(html) ? splitHtmlIntoChunks(html) : []
      })
      const htmlEntries = textEntries.length > 0 ? textEntries : block.text ? [block.text] : []

      htmlEntries.forEach((html, index) => {
        addSlide({
          html,
          id: `${block.id}-text-${index + 1}`,
          kind: 'text',
          sectionId: currentSection.id,
          slideTemplate: 'text',
          sourceBlockId: block.id,
          sourceTemplate: template,
          ...(blockTitle && index === 0 ? { title: blockTitle } : {}),
          ...(index === 0 && resolveBlockImage(block) ? { image: resolveBlockImage(block) } : {}),
        })
      })

      if (htmlEntries.length === 0) {
        skippedBlocks.push({ blockId: block.id, reason: 'empty', template })
      }
      continue
    }

    if (
      template === 'lists' ||
      template === 'highlighter' ||
      template === 'pronouncing_practice' ||
      template === 'structure' ||
      template === 'conversation' ||
      template === 'vocabulary' ||
      template === 'vocabularyWithImage' ||
      template === 'vocabularyWithoutImage'
    ) {
      const slideTemplate = resolveTeachingPresentationSlideTemplate(template)

      if (!slideTemplate) {
        skippedBlocks.push({ blockId: block.id, reason: 'unsupported_template', template })
        continue
      }

      const content = getVisibleContent(block.content)
      const cards = content.flatMap((item) =>
        template === 'conversation'
          ? resolveConversationCards(item, locale)
          : resolveCard(item, locale) ?? [],
      )

      const slideChunks = cards.length > 0
        ? slideTemplate === 'conversation'
          ? [cards]
          : chunkItems(cards)
        : []

      slideChunks.forEach((chunk, index) => {
        addSlide({
          cards: chunk,
          id: `${block.id}-cards-${index + 1}`,
          kind: 'cards',
          sectionId: currentSection.id,
          slideTemplate,
          sourceBlockId: block.id,
          sourceTemplate: template,
          ...(blockTitle && index === 0 ? { title: blockTitle } : {}),
          ...(index === 0 && resolveBlockImage(block) ? { image: resolveBlockImage(block) } : {}),
        })
      })

      if (cards.length === 0) {
        skippedBlocks.push({ blockId: block.id, reason: 'empty', template })
      }
      continue
    }

    skippedBlocks.push({
      blockId: block.id,
      reason: 'unsupported_template',
      template: String(template),
    })
  }

  return {
    lessonId,
    sections: sections.map((section) => ({
      id: section.id,
      slideIds: [...section.slideIds],
      sourceBlockIds: [...section.sourceBlockIds],
      title: section.title,
    })),
    slides,
    skippedBlocks,
    title,
  }
}
